import React from 'react';
import { Box, Typography, useTheme, Chip, Stack } from '@mui/material';
import { motion, useInView } from 'framer-motion';

const SectionTitle = ({ title, subtitle, align = 'center', chip, highlight }) => {
  const theme = useTheme();
  const ref = React.useRef();
  const isInView = useInView(ref, { once: true });

  return (
    <Box
      ref={ref}
      sx={{
        textAlign: align,
        mb: 6,
        position: 'relative',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        {chip && (
          <Chip
            label={chip}
            sx={{
              mb: 2,
              bgcolor: theme.palette.primary.light + '20',
              color: theme.palette.primary.main,
              fontWeight: 600,
            }}
          />
        )}

        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
            fontWeight: 800,
            mb: 2,
            position: 'relative',
            display: 'inline-block',
          }}
        >
          {highlight ? (
            <>
              {title.split(highlight)[0]}
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -5,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    borderRadius: 2,
                  },
                }}
              >
                {highlight}
              </Box>
              {title.split(highlight)[1]}
            </>
          ) : (
            title
          )}
        </Typography>

        {subtitle && (
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: 700,
              mx: align === 'center' ? 'auto' : 0,
              mt: 2,
              fontSize: { xs: '1rem', md: '1.1rem' },
              lineHeight: 1.6,
            }}
          >
            {subtitle}
          </Typography>
        )}

        {/* Decorative Line */}
        <Box
          sx={{
            width: 80,
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            borderRadius: 2,
            mx: align === 'center' ? 'auto' : 0,
            mt: 3,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              width: 40,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              borderRadius: 2,
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1, transform: 'scaleX(1)' },
                '50%': { opacity: 0.5, transform: 'scaleX(0.8)' },
              },
            },
          }}
        />
      </motion.div>
    </Box>
  );
};

export default SectionTitle;